import { TypeAsserter, TypeChecker } from '@attain-apps/type-guards'

import * as core from '../../core'
import * as common from '../common'

// NOTE: this is being used in the documentation
import * as calculations from './calculations'

/**
 * Similar to a {@link common.Report} but with Rate-specific information, namely
 * in the form of {@link Report.Label}
 */
export interface Report extends Report.Label, common.Report {}
export namespace Report {
  export const isReport: TypeChecker<Report> = TypeChecker.Factory.lazy(() =>
    TypeChecker.Factory.intersection(Label.isLabel, common.Report.isReport),
  )

  /**
   * Determines the structure of the report
   */

  // TODO: Use consistent names
  export enum Type {
    /**
     * Shows the starting point before we calculate any allocations. This is
     * done in three parts:
     * 1. _Reclassifications_ - Basic mappings from the institution's financial
     *    statement functions to the relevant IDC categories
     * 1. _Exclusions_ - Amounts that have been excluded from the various IDC
     *    categories, typically because they are not eligible expenses for the
     *    rate proposal
     * 1. _Adjustments_ - Other amounts to add/subtract from the various IDC
     *    categories. Some of these are system generated, and some come from the
     *    user
     */
    AllPoolMappingsSummary = 'all-pool-mappings-summ',
    /**
     * Shows total allocations calculated in the system; i.e. from every
     * indirect IDC category to every IDC category
     */
    AllAllocationsSummary = 'all-alloc-summ',
    /**
     * Presents total indirect cost rate for a given direct IDC category along
     * with the rates for each indirect IDC category
     *
     * _Has Multiple_ - One for each direct IDC category (except OIA)
     */
    RateSummary = 'rate-summ',
    /**
     * For each indirect IDC category, we have a pool of costs that we allocate
     * to other indirect IDC categories. This report is used to summarize that
     * pool of costs before we do the allocation
     */
    PoolSummary = 'pool-summ',
    /**
     * Presents {@link calculations.Allocation.Support}
     *
     * _Has Multiple_ - At least one per indirect IDC category
     */
    AllocationSupport = 'alloc-supp',
    /**
     * For IDC categories with subcategories, this report summarizes the
     * allocations of those subcategories and aggregates them to determine
     * allocations for the associated category
     *
     * _Has Multiple_ - One per indirect IDC category that has subcategories
     */
    SubpoolAllocationsSummary = 'subpool-alloc-summ',
    /**
     * For each indirect IDC category, we want to show the allocations made *to*
     * that IDC category and then the allocations made *from* that IDC category
     * to other categories
     *
     * _Has Multiple_ - One per indirect IDC category
     */
    Exchange = 'exchange',
  }
  // Needs to be before namespace
  const TYPES: ReadonlySet<Type> = new Set(Object.values(Type) as Type[])
  export namespace Type {
    export const ALL: readonly Type[] = Array.from(TYPES)
    export const isType = TypeChecker.Factory.oneOf(TYPES)
    export const assertIsType: TypeAsserter<Type> =
      TypeAsserter.Factory.from(isType)

    /**
     * DO NOT DELETE!!!
     *
     * NOTE: this is used to ensure that no {@link Type} includes a
     * {@link Separator}, as that would cause parsing issues, resulting in
     * unpredictable issues
     */
    type InvalidType = Satisfies<
      {
        [TType in Type]: TType extends `${string}${typeof Id.SEPARATOR}${string}`
          ? // If the above condition is true, this is an invalid type
            TType
          : never
      }[Type],
      // We expect to have no invalid types
      never
    >

    /**
     * {@link Type} that requires {@link Id.Info.Extra} in an {@link Id}
     */
    export type NeedingExtraInfo = {
      [TType in Type]: Id.Info.Extra<TType> extends undefined ? never : TType
    }[Type]

    /**
     * {@link Type} that *does not* require {@link Id.Info.Extra} in an {@link Id}
     */
    export type NeedingNoExtraInfo = Exclude<Type, NeedingExtraInfo>

    /**
     * Extracts the {@link Type} from an {@link Id}
     */
    export function from<TType extends Type>(id: Id<TType>): TType
    export function from(id: Id): Type {
      const separatorIdx = id.indexOf(Id.SEPARATOR)
      const type = separatorIdx === -1 ? id : id.substring(0, separatorIdx)
      assertIsType(type)
      return type
    }
  }

  /**
   * Identifies a {@link Report}. This starts with the {@link Type}, and may
   * also include a suffix that encodes {@link Id.Info}
   */
  export type Id<TType extends Type = Type> =
    TType extends Type.NeedingExtraInfo
      ? `${TType}${typeof Id.SEPARATOR}${string}`
      : TType
  export namespace Id {
    /**
     * Separates the {@link Type} from any encoded {@link Id.Info}
     */
    export const SEPARATOR = ':'

    export const isId = TypeChecker.Factory.custom(
      (val: unknown): val is Id => {
        if (!TypeChecker.isString(val)) {
          return false
        }
        const separatorIdx = val.indexOf(SEPARATOR)
        // It must either be a Type, or start with a Type
        return Type.isType(
          separatorIdx === -1 ? val : val.substring(0, separatorIdx),
        )
      },
      {
        typeDescription: 'rate.Report.Id',
      },
    )
    export const assertIsId: TypeAsserter<Id> = TypeAsserter.Factory.from(isId)

    /**
     * Creates an {@link Id} based off the {@link Type} and possibly
     * {@link Info}
     */
    export function create<TType extends Type>(info: Info<TType>): Id<TType>
    export function create({ type, extra }: Info): Id {
      const suffix = Info.Extra.Encoder.of(type).encode(extra)
      const id = suffix ? (`${type}${SEPARATOR}${suffix}` as const) : type
      // NOTE: the type for `Id` is strict about which types can and cannot have
      // suffixes, whereas the const `id` can't distinguish between types that
      // should or should not have suffixes. So we just want to make sure that
      // `id` at least might have a valid `Id`, ignoring which types can have
      // suffixes. The `Satisfies` check does this
      return id as Satisfies<Id, typeof id>
    }

    /**
     * Creates an {@link Id} based off the {@link Type}, and if it is a
     * {@link Type.WithMultipleVersions}, a {@link Suffix.Info}
     */
    export function parse<TType extends Type>(id: Id<TType>): Info<TType>
    export function parse(id: Id): Info {
      const type = Type.from(id)
      // NOTE: since the type of `Info` is distributed, TypeScript complains
      // because it doesn't realize that `extra` and `type` are guaranteed to be
      // derived from the same `Type`, as we are using
      // `Info.Extra.Encoder.of(type)`. Therefore, we need to cast
      return {
        extra: Info.Extra.Encoder.of(type).decode(
          id.substring(type.length + SEPARATOR.length),
        ),
        type,
      } satisfies FlattenUnion<Info> as Info
    }

    /**
     * All of the information encoded in a {@link Id}
     *
     * @see {@link Info.Extra}
     */
    export type Info<TType extends Type = Type> = TType extends any
      ? {
          readonly type: TType
        } & AllowMissing<{
          /**
           * @see {@link Info.Extra}
           */
          readonly extra: Readonly<Info.Extra<TType>>
        }>
      : never
    export namespace Info {
      /**
       * Extra {@link Info} encoded in the {@link Id}, specific to the given {@link Type}
       *
       * @see {@link Extra.Encoder}
       */
      export type Extra<TType extends Type = Type> = {
        [Type.AllAllocationsSummary]: undefined
        [Type.AllocationSupport]: Required.Semi<
          common.FaFunction.Categorization.Ids,
          'categoryId'
        > & {
          valueType?: calculations.Allocation.ValueType
        }
        [Type.AllPoolMappingsSummary]: undefined
        [Type.Exchange]: calculations.Pool.Id
        [Type.PoolSummary]: calculations.Pool.Id
        // NOTE: this one does not necessarily correspond to a pool, as those
        // are usually indirect categories, and this is a direct category
        [Type.RateSummary]: common.FaFunction.Category.Id
        [Type.SubpoolAllocationsSummary]: calculations.Pool.Id
      }[TType]
      export namespace Extra {
        /**
         * Used to encode/decode {@link Info} into/from a string used in an
         * {@link Id}
         */
        export type Encoder<TType extends Type = Type> = core.common.Encoder<
          Extra<TType>,
          string
        >
        export namespace Encoder {
          /**
           * Returns the {@link Id.Info.Encoder} of the given {@link Type}
           */
          export function of<TType extends Type>(type: TType): Encoder<TType> {
            return ENCODERS[type]
          }

          const UNDEFINED_ENCODER: core.common.Encoder<undefined, string> = {
            decode: () => undefined,
            encode: () => '',
          }
          const CATEGORY_ID_ENCODER: core.common.Encoder<
            common.FaFunction.Category.Id,
            string
          > = {
            decode: categoryId =>
              TypeAsserter.validateButJustWarn(
                categoryId,
                common.FaFunction.Category.Id.assertIsId,
              ),
            encode: categoryId => categoryId,
          }
          /**
           * Lookup used by {@link of}
           */
          const ENCODERS: { [TType in Type]: Encoder<TType> } = {
            [Type.AllAllocationsSummary]: UNDEFINED_ENCODER,
            [Type.AllocationSupport]: {
              decode: str => {
                const [categoryId, subcategoryId, valueType] = str.split(
                  Id.SEPARATOR,
                )
                return {
                  categoryId: TypeAsserter.validateButJustWarn(
                    categoryId,
                    common.FaFunction.Category.Id.assertIsId,
                  ),
                  subcategoryId: subcategoryId
                    ? TypeAsserter.validateButJustWarn(
                        subcategoryId,
                        common.FaFunction.Subcategory.Id.assertIsId,
                      )
                    : undefined,
                  valueType: valueType
                    ? TypeAsserter.validateButJustWarn(
                        valueType,
                        calculations.Allocation.ValueType.assertIsValueType,
                      )
                    : undefined,
                }
              },
              encode: ({ categoryId, subcategoryId, valueType }) =>
                [categoryId ?? '', subcategoryId ?? '', valueType ?? ''].join(
                  Id.SEPARATOR,
                ),
            },
            [Type.AllPoolMappingsSummary]: UNDEFINED_ENCODER,
            [Type.Exchange]: CATEGORY_ID_ENCODER,
            [Type.PoolSummary]: CATEGORY_ID_ENCODER,
            [Type.RateSummary]: CATEGORY_ID_ENCODER,
            [Type.SubpoolAllocationsSummary]: CATEGORY_ID_ENCODER,
          }
        }
      }
    }
  }

  /**
   * Reason why a {@link Report} exists
   */
  export enum Purpose {
    /**
     * {@link Report} is submitted as part of the final proposal
     */
    Proposal = 'PROPOSAL',
    /**
     * {@link Report} is used to investigate the calculations done by the system
     */
    Qa = 'QA',
  }
  // Needs to be before namespace
  const PURPOSES: ReadonlySet<Purpose> = new Set(
    Object.values(Purpose) as Purpose[],
  )
  export namespace Purpose {
    export const isPurpose = TypeChecker.Factory.oneOf(PURPOSES)
    export const assertIsPurpose: TypeAsserter<Purpose> =
      TypeAsserter.Factory.from(isPurpose)
    export const ALL = Array.from(PURPOSES) as readonly Purpose[]

    /**
     * Determines the {@link Purpose} of the {@link Report} with the given
     * {@link Id}
     */
    export function of(id: Id): Purpose {
      // TODO: currently, everything is Proposal, but this needs to be
      // changed in the future
      return Purpose.Proposal
    }
  }

  type LabelBase = core.common.Identified<Id> &
    core.common.Coded &
    core.common.Named &
    core.common.Positioned &
    core.common.Activateable
  /**
   * Often we want to know about a {@link Report} before we get all of its
   * content. This is all of the information about the report that does not
   * include its content
   */
  export interface Label extends LabelBase {
    /**
     * Can be used with the code to create a short descriptive label for this
     * report. If not given, the function categorization can be used instead of
     * the hint
     */
    hint?: string
    faFunctionCategorization?: common.FaFunction.Categorization.Ids
  }
  export namespace Label {
    export const isLabel: TypeChecker<Label> = TypeChecker.Factory.lazy(() =>
      TypeChecker.Factory.intersection(
        core.common.Identified.getTypeChecker(Id.isId),
        core.common.Coded.isCoded,
        core.common.Named.isNamed,
        core.common.Positioned.isPositioned,
        core.common.Activateable.isActivateable,
        TypeChecker.Factory.object<Difference<Label, LabelBase>>({
          faFunctionCategorization: common.FaFunction.Categorization.Ids.isIds,
          hint: TypeChecker.Factory.optional(TypeChecker.isString),
        }),
      ),
    )

    /**
     * Can be used to filter a list of {@link Label labels}
     */
    export interface Filters extends Partial<Pick<Label, 'isActive'>> {
      faFunctionCategorization?: common.FaFunction.Categorization.Ids
      purposes?: readonly Report.Purpose[]
    }
    export namespace Filters {
      export const isFilters: TypeChecker<Filters> = TypeChecker.Factory.lazy(
        () =>
          TypeChecker.Factory.object<Filters>({
            faFunctionCategorization: TypeChecker.Factory.optional(
              common.FaFunction.Categorization.Ids.isIds,
            ),
            isActive: TypeChecker.Factory.optional(TypeChecker.isBoolean),
            purposes: TypeChecker.Factory.optional(
              TypeChecker.Factory.list(Purpose.isPurpose),
            ),
          }),
      )
    }

    export type Updates = Partial<
      Pick<Label, 'code' | 'name' | 'isActive' | 'hint'>
    >
    export namespace Updates {
      export const isUpdates: TypeChecker<Updates> =
        TypeChecker.Factory.object<Updates>({
          code: TypeChecker.Factory.optional(TypeChecker.isString),
          hint: TypeChecker.Factory.optional(TypeChecker.isString),
          isActive: TypeChecker.Factory.optional(TypeChecker.isBoolean),
          name: TypeChecker.Factory.optional(TypeChecker.isString),
        })

      export type Batch = Partial<Record<Id, Updates>>
      export namespace Batch {
        export const isBatch: TypeChecker<Batch> = TypeChecker.Factory.lazy(
          () =>
            TypeChecker.Factory.record(
              Id.isId,
              TypeChecker.Factory.optional(Updates.isUpdates),
            ),
        ).usingAlias('Report.Label.Updates.Batch')
      }
    }
  }
}
